import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, MailCheck, Loader2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/constants";
import { Button } from "@/components/ui/button";

export default function VerifyEmail() {
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const [status, setStatus] = useState("idle"); // idle | verifying | done | error
  const [resending, setResending] = useState(false);
  const urlToken = params.get("token");

  const verify = async (token) => {
    setStatus("verifying");
    try {
      await api.post("/auth/verify-email", { token });
      localStorage.removeItem("sitepar_verify_token");
      await refresh();
      setStatus("done");
      setTimeout(() => nav("/onboarding"), 900);
    } catch { setStatus("error"); }
  };

  // Auto-verify when the user arrives via the emailed link (?token=...)
  useEffect(() => { if (urlToken) verify(urlToken); /* eslint-disable-line */ }, [urlToken]);

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification"); toast.success("Verification email sent — check your inbox."); }
    catch (e) {
      if (e.response?.status === 502) toast.message("Email is in test mode — links currently deliver only to the sandbox address. Use your emailed link if you received one.");
      else toast.error("Could not send email right now. Please try again.");
    }
    finally { setResending(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-sp-navy">
        <img src={IMG.blueprint} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-t from-sp-navy via-sp-navy/70 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo dark />
          <div><h2 className="text-4xl font-bold leading-tight">Secure &<br />simple.</h2><p className="mt-4 text-white/60 max-w-sm">One quick step keeps your project safe.</p></div>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white text-center">
        <div className="w-full max-w-sm">
          <div className="h-16 w-16 rounded-full bg-sp-orange/10 flex items-center justify-center mx-auto">
            {status === "done" ? <MailCheck className="h-7 w-7 text-emerald-500" /> : status === "verifying" ? <Loader2 className="h-7 w-7 text-sp-orange animate-spin" /> : <Mail className="h-7 w-7 text-sp-orange" />}
          </div>
          <h1 className="text-2xl font-bold text-sp-navy mt-5" data-testid="verify-title">
            {status === "done" ? "Email verified!" : status === "verifying" ? "Verifying your email..." : "Check your email"}
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            {status === "done" ? "Taking you to set up your project..."
              : <>We've sent a verification link to <span className="font-medium text-sp-navy">{user?.email}</span>. Click the link in that email to activate your account.</>}
          </p>
          {status !== "done" && status !== "verifying" && (
            <>
              <Button data-testid="verify-resend" onClick={resend} disabled={resending} className="mt-6 w-full h-11 bg-sp-orange hover:bg-sp-orangedark rounded-lg">
                {resending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending...</> : "Resend verification email"}
              </Button>
              <p className="text-xs text-slate-400 mt-4">Didn't get it? Check spam, or resend. The link opens Sitepar and verifies you automatically.</p>
            </>
          )}
          {status === "error" && <p className="text-sm text-red-600 mt-3" data-testid="verify-error">That verification link is invalid or has expired. Please resend a new one.</p>}
        </div>
      </div>
    </div>
  );
}
